// components/AnalysisChart.tsx
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ServiceData, TimeGrouping } from '../types/analysis.types';
import { SERVICE_COLORS } from '../utils/constants';
import { formatDateLabel } from '../utils/dateUtils';

interface AnalysisChartProps {
    isLoading: boolean;
    serviceData: ServiceData[];
    timeGrouping: TimeGrouping;
    getCompanyName: (key: string) => string;
    getSelectedGroupName: () => string;
    selectedUserGroup: string | null;
}

const AnalysisChart: React.FC<AnalysisChartProps> = ({
                                                         isLoading,
                                                         serviceData,
                                                         timeGrouping,
                                                         getCompanyName,
                                                         getSelectedGroupName,
                                                         selectedUserGroup
                                                     }) => {
    const getUniqueCompanyKeys = (): string[] => {
        const companyKeys = new Set<string>();
        serviceData.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'date' && key !== 'count' && typeof item[key] === 'number' && item[key] > 0) {
                    companyKeys.add(key);
                }
            });
        });
        return Array.from(companyKeys);
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <p>Laddar data...</p>
            </div>
        );
    }

    if (serviceData.length === 0) {
        return (
            <div className="empty-container">
                <p>
                    Ingen data tillgänglig inom valt datumintervall
                    {` för ${getSelectedGroupName()}`}
                    och valda byggnadstyper. Varje byggnad räknas en gång per dag baserat på senaste besöket från respektive företag.
                </p>
            </div>
        );
    }

    const companyKeys = getUniqueCompanyKeys();

    return (
        <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={serviceData}
                    margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tickMargin={20}
                        tickFormatter={(value) => formatDateLabel(value, timeGrouping)}
                    />
                    <YAxis label={{ value: 'Byggnadsbesök', angle: -90, position: 'insideLeft', offset: -15 }} />
                    <Tooltip
                        formatter={(value: number, name: string) => {
                            const companyName = getCompanyName(name);
                            return [`${value} besök`, companyName];
                        }}
                        labelFormatter={(label: string) => {
                            const periodLabel = timeGrouping === 'daily' ? 'Datum' :
                                timeGrouping === 'weekly' ? 'Vecka' :
                                    timeGrouping === 'monthly' ? 'Månad' : 'År';
                            return `${periodLabel}: ${formatDateLabel(label, timeGrouping)}`;
                        }}
                    />
                    <Legend
                        formatter={(value: string) => getCompanyName(value)}
                        wrapperStyle={{ paddingTop: '20px' }}
                    />
                    {companyKeys.map((companyKey, index) => (
                        <Bar
                            key={companyKey}
                            dataKey={companyKey}
                            name={companyKey}
                            stackId="visits"
                            fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                            radius={index === companyKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default AnalysisChart;